import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface DateRange {
  start: string;
  end: string;
}

interface ConversionRate {
  total_sessions: number;
  converted_sessions: number;
  conversion_rate: number;
}

interface FunnelStage {
  stage: string;
  count: number;
  percentage: number;
}

interface HeatmapPoint {
  x: number;
  y: number;
  intensity: number;
}

interface TopClick {
  element_id: string | null;
  element_text: string | null;
  page_path: string;
  count: number;
  x_coordinate?: number;
  y_coordinate?: number;
}

export const useConversionRate = (dateRange: DateRange) => {
  const [data, setData] = useState<ConversionRate>({ 
    total_sessions: 0, 
    converted_sessions: 0, 
    conversion_rate: 0 
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: result, error: err } = await supabase.rpc('get_conversion_rate', {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      if (err) throw err;
      
      const conversionData = result?.[0] as ConversionRate || { 
        total_sessions: 0, 
        converted_sessions: 0, 
        conversion_rate: 0 
      };
      
      console.log('Conversion rate data:', conversionData);
      setData(conversionData);
    } catch (err) {
      console.error('Erro ao buscar conversion rate:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange.start, dateRange.end]);

  return { data, isLoading, error, refetch: fetchData };
};

export const useConversionFunnel = (dateRange: DateRange) => {
  const [data, setData] = useState<FunnelStage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: result, error: err } = await supabase.rpc('get_conversion_funnel', {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      if (err) throw err;
      
      const funnelData = (result as FunnelStage[]) || [];
      console.log('Conversion funnel data:', funnelData);
      setData(funnelData);
    } catch (err) {
      console.error('Erro ao buscar conversion funnel:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange.start, dateRange.end]);

  return { data, isLoading, error, refetch: fetchData };
};

export const useHeatmapData = (pagePath: string, dateRange: DateRange) => {
  const [data, setData] = useState<HeatmapPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: result, error: err } = await supabase.rpc('get_heatmap_data', {
        page_path: pagePath,
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      if (err) throw err;
      
      const heatmapData = (result as HeatmapPoint[]) || [];
      console.log('Heatmap data:', heatmapData);
      setData(heatmapData);
    } catch (err) {
      console.error('Erro ao buscar heatmap data:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagePath, dateRange.start, dateRange.end]);

  return { data, isLoading, error, refetch: fetchData };
};

export const useTopClicks = (dateRange: DateRange, limit = 10) => {
  const [data, setData] = useState<TopClick[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: result, error: err } = await supabase
        .from('public_page_events')
        .select('element_id, element_text, page_path, x_coordinate, y_coordinate')
        .eq('event_type', 'click')
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end);
      
      if (err) throw err;
      
      const grouped = (result || []).reduce((acc: Record<string, TopClick>, item) => {
        const key = `${item.element_id}-${item.element_text}`;
        if (!acc[key]) {
          acc[key] = { ...item, count: 0 };
        }
        acc[key].count++;
        return acc;
      }, {});
      
      const topClicksData = Object.values(grouped)
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);
      
      console.log('Top clicks data:', topClicksData);
      setData(topClicksData);
    } catch (err) {
      console.error('Erro ao buscar top clicks:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange.start, dateRange.end, limit]);

  return { data, isLoading, error, refetch: fetchData };
};

export const useVisitorsOverTime = (dateRange: DateRange) => {
  const [data, setData] = useState<Array<{ date: string; visitors: number }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: result, error: err } = await supabase
        .from('public_page_events')
        .select('created_at, session_id')
        .eq('event_type', 'page_view')
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end)
        .order('created_at', { ascending: true });
      
      if (err) throw err;
      
      const groupedByDate = (result || []).reduce((acc: Record<string, Set<string>>, item) => {
        const date = new Date(item.created_at).toLocaleDateString();
        if (!acc[date]) {
          acc[date] = new Set();
        }
        acc[date].add(item.session_id);
        return acc;
      }, {});
      
      const visitorsData = Object.entries(groupedByDate).map(([date, sessions]) => ({
        date,
        visitors: sessions.size
      }));
      
      console.log('Visitors over time data:', visitorsData);
      setData(visitorsData);
    } catch (err) {
      console.error('Erro ao buscar visitors over time:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange.start, dateRange.end]);

  return { data, isLoading, error, refetch: fetchData };
};

export const useAverageSessionTime = (dateRange: DateRange) => {
  const [data, setData] = useState<{ average_minutes: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: result, error: err } = await supabase
        .from('public_sessions')
        .select('first_seen_at, last_seen_at')
        .gte('first_seen_at', dateRange.start)
        .lte('first_seen_at', dateRange.end)
        .not('last_seen_at', 'is', null);
      
      if (err) throw err;
      
      if (!result || result.length === 0) {
        setData({ average_minutes: 0 });
        return;
      }
      
      const durations = result.map(session => {
        const start = new Date(session.first_seen_at).getTime();
        const end = new Date(session.last_seen_at).getTime();
        return (end - start) / 1000 / 60;
      });
      
      const average = durations.reduce((sum, d) => sum + d, 0) / durations.length;
      
      console.log('Average session time:', average);
      setData({ average_minutes: average });
    } catch (err) {
      console.error('Erro ao buscar tempo médio:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange.start, dateRange.end]);

  return { data, isLoading, error, refetch: fetchData };
};
